"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { useMemo } from "react";
import clsx from "clsx";
import type { ApiXTdhCollectionsPage } from "@/generated/models/ApiXTdhCollectionsPage";
import { useContractOverviewQuery } from "@/hooks/useAlchemyNftQueries";
import { isValidEthAddress } from "@/helpers/Helpers";
import { shortenAddress } from "@/helpers/address.helpers";
import { formatFloorPrice, formatTotalSupply } from "@/components/user/xtdh/granted-list/subcomponents/UserPageXtdhGrantListItem/formatters";
import { formatCount, formatXtdhRate, formatXtdhValue } from "../utils/formatters";
import { XtdhRatePill } from "./subcomponents/XtdhRatePill";
type ApiXtdhCollection = ApiXTdhCollectionsPage["data"][number];
type InteractionMode = "button" | "static";
interface XtdhReceivedCollectionCardProps {
  readonly collection: ApiXtdhCollection;
  readonly isSelected?: boolean | undefined;
  readonly onSelect?: ((contract: string | null) => void) | undefined | undefined;
  readonly interactionMode?: InteractionMode | undefined;
}
export function XtdhReceivedCollectionCard(t0) {
  const $ = _c(95);
  const {
    collection,
    isSelected: t1,
    onSelect,
    interactionMode: t2
  } = t0;
  const isSelected = t1 === undefined ? false : t1;
  const interactionMode = t2 === undefined ? "button" : t2;
  let t3;
  if ($[0] !== collection.contract) {
    const trimmed = collection.contract?.trim() ?? "";
    t3 = isValidEthAddress(trimmed) ? trimmed as `0x${string}` : null;
    $[0] = collection.contract;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const contractAddress = t3;
  const t4 = contractAddress ?? undefined;
  const t5 = Boolean(contractAddress);
  let t6;
  if ($[2] !== t4 || $[3] !== t5) {
    t6 = {
      address: t4,
      enabled: t5
    };
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const {
    data: contract,
    isLoading,
    isError
  } = useContractOverviewQuery(t6);
  contract?.name;
  let t7;
  bb0: {
    if (contract?.name) {
      t7 = contract.name;
      break bb0;
    }
    if (contractAddress) {
      let t8;
      if ($[5] !== contractAddress) {
        t8 = shortenAddress(contractAddress);
        $[5] = contractAddress;
        $[6] = t8;
      } else {
        t8 = $[6];
      }
      t7 = t8;
      break bb0;
    }
    t7 = "Unknown collection";
  }
  const displayName = t7;
  let t8;
  bb1: {
    if (isLoading) {
      let t9;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = {
          secondaryLabel: "Loading metadata...",
          secondaryLabelTitle: undefined
        };
        $[7] = t9;
      } else {
        t9 = $[7];
      }
      t8 = t9;
      break bb1;
    }
    if (isError) {
      let t9;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = {
          secondaryLabel: "Metadata unavailable",
          secondaryLabelTitle: undefined
        };
        $[8] = t9;
      } else {
        t9 = $[8];
      }
      t8 = t9;
      break bb1;
    }
    if (contractAddress) {
      let normalized;
      let t9;
      if ($[9] !== contractAddress) {
        normalized = contractAddress.toLowerCase();
        t9 = shortenAddress(normalized);
        $[9] = contractAddress;
        $[10] = normalized;
        $[11] = t9;
      } else {
        normalized = $[10];
        t9 = $[11];
      }
      let t10;
      if ($[12] !== normalized || $[13] !== t9) {
        t10 = {
          secondaryLabel: t9,
          secondaryLabelTitle: normalized
        };
        $[12] = normalized;
        $[13] = t9;
        $[14] = t10;
      } else {
        t10 = $[14];
      }
      t8 = t10;
      break bb1;
    }
    const fallback = collection.contract ?? "Unknown contract";
    let t9;
    if ($[15] !== fallback) {
      t9 = {
        secondaryLabel: fallback,
        secondaryLabelTitle: fallback
      };
      $[15] = fallback;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    t8 = t9;
  }
  const {
    secondaryLabel,
    secondaryLabelTitle
  } = t8;
  const imageUrl = contract?.imageUrl ?? null;
  const tokenTypeLabel = contract?.tokenType ?? "Unknown";
  const t9 = contract?.totalSupply;
  let t10;
  if ($[17] !== t9) {
    t10 = formatTotalSupply(t9);
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const totalSupplyLabel = t10;
  const t11 = contract?.floorPriceEth;
  let t12;
  if ($[19] !== t11) {
    t12 = formatFloorPrice(t11);
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const floorPriceLabel = t12;
  let t13;
  if ($[21] !== collection.total_token_count) {
    t13 = formatCount(collection.total_token_count);
    $[21] = collection.total_token_count;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const totalTokensGrantedLabel = t13;
  let t14;
  if ($[23] !== collection.active_token_count) {
    t14 = formatCount(collection.active_token_count);
    $[23] = collection.active_token_count;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const activeTokensGrantedLabel = t14;
  let t15;
  if ($[25] !== collection.total_contributor_count) {
    t15 = formatCount(collection.total_contributor_count);
    $[25] = collection.total_contributor_count;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const totalContributorsLabel = t15;
  let t16;
  if ($[27] !== collection.active_contributor_count) {
    t16 = formatCount(collection.active_contributor_count);
    $[27] = collection.active_contributor_count;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const activeContributorsLabel = t16;
  let t17;
  if ($[29] !== collection.xtdh) {
    t17 = formatXtdhValue(collection.xtdh);
    $[29] = collection.xtdh;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const xtdhValueLabel = t17;
  let t18;
  if ($[31] !== collection.xtdh_rate) {
    t18 = formatXtdhRate(collection.xtdh_rate);
    $[31] = collection.xtdh_rate;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const xtdhRateLabel = t18;
  const isSelectable = interactionMode === "button" && Boolean(onSelect && collection.contract);
  const t19 = interactionMode === "static" ? "tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900 tw-p-4" : "tw-rounded-b-xl tw-px-6 tw-py-6 desktop-hover:hover:tw-bg-iron-900";
  const t20 = isSelected && "tw-bg-iron-900/50";
  const t21 = !isSelectable && interactionMode === "button" && "tw-cursor-not-allowed tw-opacity-70";
  let t22;
  if ($[33] !== t19 || $[34] !== t20 || $[35] !== t21) {
    t22 = clsx("tw-group tw-flex tw-w-full tw-flex-col tw-items-stretch tw-overflow-hidden tw-text-left tw-transition-colors tw-duration-300", t19, t20, t21);
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== displayName || $[38] !== imageUrl) {
    t23 = <div className="tw-relative tw-h-16 tw-w-16 tw-overflow-hidden tw-rounded-lg tw-bg-iron-800 tw-shadow-inner tw-transition-transform tw-duration-300 group-hover:tw-scale-105">{imageUrl ? <Image src={imageUrl} alt={displayName} fill={true} sizes="64px" className="tw-h-full tw-w-full tw-object-cover" /> : <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-400">NFT</div>}</div>;
    $[37] = displayName;
    $[38] = imageUrl;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== displayName) {
    t24 = <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-iron-50">{displayName}</p>;
    $[40] = displayName;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== secondaryLabel || $[43] !== secondaryLabelTitle) {
    t25 = <p className="tw-m-0 tw-mb-0 tw-text-xs tw-text-iron-500" title={secondaryLabelTitle}>{secondaryLabel}</p>;
    $[42] = secondaryLabel;
    $[43] = secondaryLabelTitle;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== tokenTypeLabel) {
    t26 = <span className="tw-inline-flex tw-items-center tw-rounded tw-border tw-border-solid tw-border-white/10 tw-bg-white/5 tw-px-2 tw-py-1 tw-text-[10px] tw-font-bold tw-uppercase tw-text-iron-400">{tokenTypeLabel}</span>;
    $[45] = tokenTypeLabel;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== t25 || $[48] !== t26) {
    t27 = <div className="tw-flex tw-w-full tw-items-center tw-gap-2">{t25}{t26}</div>;
    $[47] = t25;
    $[48] = t26;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== t24 || $[51] !== t27) {
    t28 = <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">{t24}{t27}</div>;
    $[50] = t24;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== t23 || $[54] !== t28) {
    t29 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center">{t23}{t28}</div>;
    $[53] = t23;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== xtdhRateLabel || $[57] !== xtdhValueLabel) {
    t30 = <XtdhRatePill rateLabel={xtdhRateLabel} totalLabel={xtdhValueLabel} />;
    $[56] = xtdhRateLabel;
    $[57] = xtdhValueLabel;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== t29 || $[60] !== t30) {
    t31 = <header className="tw-flex tw-flex-col tw-items-start tw-justify-between tw-gap-x-3 tw-gap-y-6 md:tw-flex-row">{t29}{t30}</header>;
    $[59] = t29;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== totalSupplyLabel) {
    t32 = <CollectionMetric label="Total supply" value={totalSupplyLabel} />;
    $[62] = totalSupplyLabel;
    $[63] = t32;
  } else {
    t32 = $[63];
  }
  let t33;
  if ($[64] !== activeTokensGrantedLabel) {
    t33 = <CollectionMetric label="Active tokens" value={activeTokensGrantedLabel} />;
    $[64] = activeTokensGrantedLabel;
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] !== totalTokensGrantedLabel) {
    t34 = <CollectionMetric label="Tokens granted" value={totalTokensGrantedLabel} />;
    $[66] = totalTokensGrantedLabel;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] !== floorPriceLabel) {
    t35 = <CollectionMetric label="Floor price" value={floorPriceLabel} />;
    $[68] = floorPriceLabel;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] !== activeContributorsLabel) {
    t36 = <CollectionMetric label="Active contributors" value={activeContributorsLabel} />;
    $[70] = activeContributorsLabel;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] !== totalContributorsLabel) {
    t37 = <CollectionMetric label="Contributors" value={totalContributorsLabel} />;
    $[72] = totalContributorsLabel;
    $[73] = t37;
  } else {
    t37 = $[73];
  }
  let t38;
  if ($[74] !== t32 || $[75] !== t33 || $[76] !== t34 || $[77] !== t35 || $[78] !== t36 || $[79] !== t37) {
    t38 = <dl className="tw-mb-0 tw-mt-6 tw-grid tw-gap-x-6 tw-gap-y-4 sm:tw-grid-cols-2 md:tw-ml-20 md:tw-mt-4 md:tw-grid-cols-3">{t32}{t33}{t34}{t35}{t36}{t37}</dl>;
    $[74] = t32;
    $[75] = t33;
    $[76] = t34;
    $[77] = t35;
    $[78] = t36;
    $[79] = t37;
    $[80] = t38;
  } else {
    t38 = $[80];
  }
  let t39;
  if ($[81] !== t22 || $[82] !== t31 || $[83] !== t38) {
    t39 = <div className={t22}>{t31}{t38}</div>;
    $[81] = t22;
    $[82] = t31;
    $[83] = t38;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  const content = t39;
  if (interactionMode === "static") {
    let t40;
    if ($[85] !== content) {
      t40 = <li className="tw-list-none">{content}</li>;
      $[85] = content;
      $[86] = t40;
    } else {
      t40 = $[86];
    }
    return t40;
  }
  let t40;
  if ($[87] !== collection.contract || $[88] !== onSelect) {
    t40 = () => {
      if (!onSelect || !collection.contract) {
        return;
      }
      onSelect(collection.contract);
    };
    $[87] = collection.contract;
    $[88] = onSelect;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  const handleSelect = t40;
  const t41 = !isSelectable;
  let t42;
  if ($[90] !== content || $[91] !== handleSelect || $[92] !== isSelected || $[93] !== t41) {
    t42 = <li className="tw-list-none"><button type="button" onClick={handleSelect} disabled={t41} aria-pressed={isSelected} className="tw-w-full tw-border-none tw-bg-transparent tw-p-0 tw-text-left">{content}</button></li>;
    $[90] = content;
    $[91] = handleSelect;
    $[92] = isSelected;
    $[93] = t41;
    $[94] = t42;
  } else {
    t42 = $[94];
  }
  return t42;
}
function CollectionMetric(t0) {
  const $ = _c(10);
  const {
    label,
    value,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = clsx("tw-flex tw-flex-col tw-text-left", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <dt className="tw-mb-1.5 tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-iron-500">{label}</dt>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== value) {
    t3 = <dd className="tw-m-0 tw-text-base tw-font-semibold tw-text-iron-50">{value}</dd>;
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}